import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
// import QRCode from 'qrcode.react';
import QRCode from 'react-qr-code';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { paymentAPI } from '../../api';

function CryptoPayment() {
  const { invoiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // If we have details from navigation state
        if (location.state) {
          setPaymentDetails(location.state);
        } else {
          // Fetch from API
          const response = await paymentAPI.getInvoice(invoiceId);
          if (response.success) {
            setPaymentDetails(response.data);
          } else {
            toast.error('Failed to load payment details');
          }
        }
      } catch (error) {
        console.error('Error loading payment details:', error);
        toast.error('Failed to load payment details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [invoiceId, location.state]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Payment not found</h2>
          <button
            onClick={() => navigate('/cbt_creator/payment')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Go back to payment page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Complete Crypto Payment</h1>
          <p className="text-gray-600 mt-2">Send crypto to the address below</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: Payment Details */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4">Payment Information</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Invoice ID:</span>
                    <span className="font-mono font-bold text-gray-800">{invoiceId}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Amount:</span>
                    <span className="text-2xl font-bold text-gray-800">
                      {paymentDetails.amount} {paymentDetails.currency}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Network:</span>
                    <span className="font-semibold text-gray-800">{paymentDetails.network}</span>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Wallet Address</h4>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Send to:</span>
                    <button
                      onClick={() => copyToClipboard(paymentDetails.wallet_address)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="font-mono text-gray-800 break-all">
                    {paymentDetails.wallet_address}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-bold text-blue-800 mb-2">Important Instructions:</h4>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• Send <strong>exactly {paymentDetails.amount} {paymentDetails.currency}</strong></li>
                  <li>• Use <strong>{paymentDetails.network}</strong> network only</li>
                  <li>• Payment will be confirmed automatically</li>
                  <li>• Subscription activates within 24 hours after confirmation</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Right Column: QR Code & Status */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Scan QR Code</h3>
              <div className="flex justify-center">
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <QRCode
                    value={paymentDetails.wallet_address}
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>
              <p className="text-center text-gray-600 mt-4 text-sm">
                Scan with your crypto wallet to get the address
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
              <h3 className="text-lg font-bold mb-4">Payment Status</h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3 animate-pulse"></div>
                  <span>Waiting for payment...</span>
                </div>
                <div className="flex items-center opacity-50">
                  <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                  <span>Payment detected</span>
                </div>
                <div className="flex items-center opacity-50">
                  <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                  <span>Confirming on blockchain</span>
                </div>
                <div className="flex items-center opacity-50">
                  <div className="w-3 h-3 bg-gray-300 rounded-full mr-3"></div>
                  <span>Subscription activated</span>
                </div>
              </div>
              <p className="mt-6 text-blue-100 text-sm">
                Payments are checked automatically every 15 minutes
              </p>
            </div>

            <div className="flex gap-4">
<a
  href="https://wa.me/2348113315252"
  target="_blank"
  rel="noopener noreferrer"
  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-semibold"
>
  <i className="fab fa-whatsapp text-lg"></i>
  Chat on WhatsApp
</a>


              <button
                onClick={() => navigate('/cbt_creator/payment')}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold"
              >
                Back to Payment
              </button>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default CryptoPayment;